import Phaser from "phaser";

import { ARENA_ART } from "../config/artAssets";
import type { PvpMatchState, PvpTurnResult } from "../domain/types";
import {
  PenaltyArenaScene,
  type PenaltyArenaLoadProgress,
  type PenaltyArenaTargetLayout,
  type PenaltyArenaVisualState,
} from "./PenaltyArenaScene";
import { gameRenderScale } from "./renderScale";

export type PenaltyArenaRuntime = {
  game: Phaser.Game;
  scene: PenaltyArenaScene;
  ready: Promise<void>;
  setMatch(match: PvpMatchState, reducedMotion: boolean): void;
  playMatchFound(): void;
  playTurn(result: PvpTurnResult): void;
  playResult(result: PvpMatchState["result"]): void;
  setPaused(paused: boolean): void;
  destroy(): void;
};

export type PenaltyArenaRuntimeCallbacks = {
  onLoadProgress?(progress: PenaltyArenaLoadProgress): void;
  onTargetLayout?(layout: PenaltyArenaTargetLayout): void;
  onVisualIssue?(code: "ARENA_ASSET_LOAD" | "ARENA_FRAME_EMPTY" | "ARENA_FRAME_UNREADABLE", source: string): void;
  onVisualRecovery?(): void;
};

function canvasHasPaintedBackground(canvas: HTMLCanvasElement): boolean | null {
  try {
    const context = canvas.getContext("2d");
    if (context === null) return null;
    return [0.2, 0.5, 0.8].some((fraction) =>
      (context.getImageData(Math.floor(canvas.width / 2), Math.floor(canvas.height * fraction), 1, 1).data[3] ?? 0) > 32);
  } catch {
    return null;
  }
}

export function createPenaltyArenaGame(
  parent: HTMLElement,
  match: PvpMatchState,
  reducedMotion: boolean,
  callbacks: PenaltyArenaRuntimeCallbacks = {},
): PenaltyArenaRuntime {
  const renderScale = gameRenderScale(window.devicePixelRatio || 1, document.documentElement.dataset.platform);
  const fallbackWidth = 360;
  const fallbackHeight = 746;
  const width = Math.max(1, parent.clientWidth || fallbackWidth);
  const height = Math.max(540, parent.clientHeight || fallbackHeight);
  let resolveReady: () => void = () => undefined;
  let sceneReady = false;
  let destroyed = false;
  let firstFrameReady = false;
  let emptyFrames = 0;
  let lastFrameCheckAt = 0;
  let frameFallbackActive = false;
  let backgroundAssetFailed = false;
  let currentMatch = match;
  let pendingMatchFoundFor: string | null = null;
  let pendingTurn: { matchId: string | null; result: PvpTurnResult } | null = null;
  let pendingResult: { matchId: string | null; result: PvpMatchState["result"] } | null = null;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const flushPendingPresentation = (): void => {
    if (!sceneReady || destroyed) return;
    const matchId = currentMatch.matchId;
    if (pendingMatchFoundFor === matchId) scene.playMatchFound();
    if (pendingTurn?.matchId === matchId) scene.playTurn(pendingTurn.result);
    if (pendingResult?.matchId === matchId) scene.playResult(pendingResult.result);
    pendingMatchFoundFor = null;
    pendingTurn = null;
    pendingResult = null;
  };
  const visualState: PenaltyArenaVisualState = { match, reducedMotion, paused: false };
  const scene = new PenaltyArenaScene(
    {
      onReady: () => {
        sceneReady = true;
        flushPendingPresentation();
      },
      onLoadProgress: (progress) => callbacks.onLoadProgress?.(progress),
      onAssetError: (key) => {
        if (key === ARENA_ART.background.key || key === ARENA_ART.defenseBackground.key) backgroundAssetFailed = true;
        callbacks.onVisualIssue?.("ARENA_ASSET_LOAD", key);
      },
      onTargetLayout: (layout) => callbacks.onTargetLayout?.(layout),
    },
    visualState,
    renderScale,
  );
  let paused = false;
  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    parent,
    width: Math.round(width * renderScale),
    height: Math.round(height * renderScale),
    transparent: true,
    antialias: true,
    render: { antialias: true, pixelArt: false, roundPixels: false },
    scale: { mode: Phaser.Scale.NONE, autoCenter: Phaser.Scale.NO_CENTER },
    physics: {
      default: "arcade",
      arcade: { debug: false, gravity: { x: 0, y: 0 } },
    },
    scene: [scene],
    banner: false,
  });
  game.canvas.style.width = "100%";
  game.canvas.style.height = "100%";
  game.canvas.style.display = "block";
  game.canvas.setAttribute("aria-hidden", "true");
  game.canvas.style.pointerEvents = "none";
  let readyWatchdog = window.setTimeout(function checkReady() {
    if (destroyed || firstFrameReady) return;
    if (document.hidden) {
      readyWatchdog = window.setTimeout(checkReady, 3_000);
      return;
    }
    firstFrameReady = true;
    frameFallbackActive = true;
    callbacks.onVisualIssue?.("ARENA_FRAME_EMPTY", "timeout");
    resolveReady();
  }, 6_000);

  const inspectFrame = (): void => {
    if (!sceneReady || destroyed || document.hidden) return;
    const now = performance.now();
    if (firstFrameReady && emptyFrames === 0 && now - lastFrameCheckAt < 15_000) return;
    lastFrameCheckAt = now;
    const painted = canvasHasPaintedBackground(game.canvas);
    if (painted === true) {
      emptyFrames = 0;
      if (frameFallbackActive && !backgroundAssetFailed) {
        frameFallbackActive = false;
        callbacks.onVisualRecovery?.();
      }
    } else emptyFrames += 1;
    if (!firstFrameReady) {
      firstFrameReady = true;
      window.clearTimeout(readyWatchdog);
      resolveReady();
    }
    if (frameFallbackActive || emptyFrames < 2) return;
    frameFallbackActive = true;
    callbacks.onVisualIssue?.(painted === null ? "ARENA_FRAME_UNREADABLE" : "ARENA_FRAME_EMPTY", "canvas");
  };
  game.events.on(Phaser.Core.Events.POST_RENDER, inspectFrame);

  const resizeObserver = typeof ResizeObserver === "undefined"
    ? null
    : new ResizeObserver(([entry]) => {
        if (entry === undefined) return;
        const nextWidth = Math.max(1, Math.round(entry.contentRect.width));
        const nextHeight = Math.max(540, Math.round(entry.contentRect.height));
        game.scale.resize(Math.round(nextWidth * renderScale), Math.round(nextHeight * renderScale));
      });
  resizeObserver?.observe(parent);

  return {
    game,
    scene,
    ready,
    setMatch(nextMatch, nextReducedMotion): void {
      const previousMatchId = currentMatch.matchId;
      currentMatch = nextMatch;
      if (nextMatch.matchId !== previousMatchId) {
        pendingMatchFoundFor = null;
        pendingTurn = null;
        pendingResult = null;
      }
      scene.setVisualState({ match: nextMatch, reducedMotion: nextReducedMotion, paused });
    },
    playMatchFound(): void {
      if (sceneReady) scene.playMatchFound();
      else pendingMatchFoundFor = currentMatch.matchId;
    },
    playTurn(result): void {
      if (sceneReady) scene.playTurn(result);
      else pendingTurn = { matchId: currentMatch.matchId, result };
    },
    playResult(result): void {
      if (sceneReady) scene.playResult(result);
      else pendingResult = { matchId: currentMatch.matchId, result };
    },
    setPaused(nextPaused): void {
      paused = nextPaused;
      scene.setPaused(nextPaused);
    },
    destroy(): void {
      destroyed = true;
      pendingMatchFoundFor = null;
      pendingTurn = null;
      pendingResult = null;
      resizeObserver?.disconnect();
      window.clearTimeout(readyWatchdog);
      game.events.off(Phaser.Core.Events.POST_RENDER, inspectFrame);
      game.destroy(true);
    },
  };
}

export function warmPenaltyArenaAssets(urls: readonly string[]): Promise<void> {
  return Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve) => {
          const image = new Image();
          image.decoding = "async";
          image.onload = () => resolve();
          image.onerror = () => resolve();
          image.src = url;
          if (image.complete) resolve();
        }),
    ),
  ).then(() => undefined);
}
